import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Grid, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography } from "@mui/material";
import Icon from "@mdi/react";
import { mdiClose } from "@mdi/js";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../components/context/SnackbarContext";
import useAxios from "../../hooks/useAxios";
import { useLoader } from "../../components/context/LoaderContext";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { capitalize, displayDate } from "../../utils/Utils";
import TopMenu from "./components/TopMenu";
import ReactECharts from 'echarts-for-react';
import SalesQuoteList from "./SalesQuoteList";


function SalesQuota(props) {
    const navigate = useNavigate();
    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();
    const { preferredDateFormat, currencyCode } = useSelector((state) => state?.user);
    const [clearType, setClearType] = useState(false);
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [searchUser, setSearchUser] = useState("All Users");

    const [userLists, setUserLists] = useState([]);
    const [salesChartDatas, setSalesChartDatas] = useState([]);

    useEffect(() => {
        async function fetchData() {
            showLoader();
            await getData();
        }
        fetchData();
    }, [startDate, endDate, searchUser, clearType]);

    const getData = async () => {
        const params = {
            user: searchUser === "All Users" ? "" : searchUser,
            start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
            end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
        };
        showLoader()
        await axiosInstance.get(`api/dashboard-chart`, { params }).then(response => {
            if (response.status === 200) {
                setSalesChartDatas(response.data.data.salesQuotaChartData)
                setUserLists(response?.data?.data?.users || []);
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                        navigate("/dashboard");
                    }
                }
            });
    };


    const option = {
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
                label: {
                    show: true,
                },
            },
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: { show: true },
            },
        },
        calculable: true,
        legend: {
            data: ["Growth", "Budget 2011", "Budget 2012"],
            itemGap: 5,
        },
        grid: {
            top: "12%",
            left: "1%",
            right: "10%",
            containLabel: true,
        },
        xAxis: [
            {
                type: "value",
                name: currencyCode,
                axisLabel: {
                    formatter: function (a) {
                        a = +a;
                        return a ? a : "";
                    },
                },
            },
        ],
        yAxis: [
            {
                type: "category",
                data: salesChartDatas.name,
            },
        ],
        dataZoom: [

            {
                //for Y-axis zoom
                show: true,
                yAxisIndex: 0,
                filterMode: "empty",
                width: 30,
                height: "80%",
                showDataShadow: false,
                left: "93%",
            },
            {
                type: "inside",
                yAxisIndex: 0,
                filterMode: "empty",
                start: 0,
                end: 100,
            },
        ],
        series: [
            {
                name: "Sales Target",
                type: "bar",
                data: salesChartDatas.target,
            },
            {
                name: "Achieved Sales Target",
                type: "bar",
                data: salesChartDatas.achieved,
            },
        ],
    };

    const clearUserFilter = (type) => {
        setClearType(clearType ? false : true)
        if (type === 'user') {
            setSearchUser('All Users')
        } else {
            setSearchUser('All Users')
            setStartDate()
            setEndDate()
        }
    }

    return (
        <>
            <Helmet>
                <title>Sales Quota | {capitalize(props?.name || 'SolvCRM')}</title>
            </Helmet>

            <TopMenu page={'sales-quota'} />

            <Grid container className='page-action-bar'>
                <Grid item xl={'auto'} xs={12}>
                    <Stack direction="row" spacing={1} alignItems={'center'}>
                        <TextField
                            select
                            value={searchUser}
                            variant="filled"
                            className='size-small no-label no-margin'
                            onChange={(e) => setSearchUser(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end' className='clear-filter'>
                                        {searchUser && searchUser !== 'All Users' &&
                                            <IconButton onClick={() => clearUserFilter('user')} >
                                                <Icon path={mdiClose} color={'currentColor'} size={'20px'} />
                                            </IconButton>
                                        }
                                    </InputAdornment>
                                ),
                            }}
                        >
                            <MenuItem key={222} value={'All Users'}>All Users</MenuItem>
                            {
                                userLists && userLists.length > 0 && userLists.map((user, index) => (
                                    <MenuItem key={index} value={user.id}>{user.name}</MenuItem>
                                ))
                            }
                        </TextField>

                    </Stack>
                </Grid>
                {

                    ((searchUser && searchUser !== 'All Users') || startDate || endDate) &&
                    <a href="-" className="primary-link ml-12" onClick={e => { e.preventDefault(); clearUserFilter('all') }}><Typography variant='body-medium-medium'>Clear All</Typography></a>
                }
                <Grid item xl={'auto'} xs={12} sx={{ marginLeft: { xl: 'auto' }, marginTop: { xl: 0, xs: 1.5 } }}>
                    <Grid container spacing={1}>
                        <Grid item xs="auto">
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    format={displayDate(preferredDateFormat, 'half')}
                                    views={['year', 'month', 'day']}
                                    slotProps={{
                                        textField: {
                                            variant: 'filled',
                                            name: "start_date",
                                            type: 'text',
                                            placeholder: 'From Date',
                                            className: 'no-label no-margin size-small width-160'
                                        }
                                    }}
                                    name="start_date"
                                    value={startDate}
                                    onChange={(value) => setStartDate(value)}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs="auto">
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    format={displayDate(preferredDateFormat, 'half')}
                                    views={['year', 'month', 'day']}
                                    minDate={startDate}
                                    slotProps={{
                                        textField: {
                                            variant: 'filled',
                                            name: "end_date",
                                            type: 'text',
                                            placeholder: 'To Date',
                                            className: 'no-label no-margin size-small width-160'
                                        }
                                    }}
                                    name="end_date"
                                    value={endDate}
                                    onChange={(value) => setEndDate(value)}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xxl={12} xs={12}>
                    <div className="card p-16 height-100p flex">
                        <Typography variant="title-medium-bold" component={'h6'} className="pb-16">Sales Target Quota</Typography>

                        <ReactECharts
                            option={option}
                            notMerge={true}
                            lazyUpdate={true}
                            theme={"theme_name"}
                            onChartReady={""}
                            onEvents={""}
                            opts={""}
                            style={{ height: "500px" }}
                        />
                    </div>
                </Grid>
            </Grid>

            <Grid item xxl={4} xs={12}><br></br></Grid>

            <Grid container spacing={2}>
                <Grid item xxl={12} xs={12}>
                    <div className="card p-16 height-100p flex">
                        <Typography variant="title-medium-bold" component={'h6'} className="pb-16">Sales Quota History</Typography>

                        <SalesQuoteList
                            userID={searchUser === "All Users" ? "" : searchUser}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    </div>
                </Grid>
            </Grid>


        </>
    );
}

export default SalesQuota;